import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { DatePicker } from '@/components/atoms/DatePicker';
import { Textarea } from '@/components/atoms/Textarea';
import {
  TickerAutocomplete,
  type TickerMeta,
} from '@/components/molecules/TickerAutocomplete';
import { queryKeys } from '@/api/queryKeys';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { toast } from '@/store/toastStore';

/**
 * 매수 이유 선택 칩 — 백엔드 BUY_REASON_VALUES / DB CHECK 와 동기화.
 * 값 추가 시 반드시 세 곳 동시 갱신 (프론트/백엔드/migration).
 */
type BuyReason = 'earnings' | 'technical' | 'news' | 'theme' | 'other';
const BUY_REASON_CHIPS: Array<{ value: BuyReason; label: string }> = [
  { value: 'earnings',  label: '실적 개선' },
  { value: 'technical', label: '기술적 분석' },
  { value: 'news',      label: '증시뉴스·공시 분석' },
  { value: 'theme',     label: '테마' },
  { value: 'other',     label: '기타' },
];

/**
 * 자산 종류별 가격/수량 허용 범위 — 백엔드 PRICE_RANGES / QUANTITY_RANGE 와 동기화.
 * 허위 입력 방지를 위한 sanity check.
 */
const PRICE_RANGES: Record<'stock' | 'coin' | 'us_stock', { min: number; max: number }> = {
  stock: { min: 100, max: 5_000_000 },
  coin: { min: 0.000001, max: 200_000 },
  us_stock: { min: 0.01, max: 100_000 },
};
const QUANTITY_RANGE = { min: 0.001, max: 9_999_999 } as const;

/** 자산 단위 prefix */
function priceUnit(assetType: 'stock' | 'coin' | 'us_stock'): string {
  return assetType === 'us_stock' ? '$' : assetType === 'stock' ? '₩' : '';
}
function priceSuffix(assetType: 'stock' | 'coin' | 'us_stock'): string {
  return assetType === 'stock' ? '원' : '';
}

/** 종목명 표시 헬퍼 — 코인은 COIN_NAMES_KR 우선, 미장은 name_en 이 없어도 name 을 그대로 사용 */
function tickerLabelOf(
  code: string,
  name: string,
  assetType: 'stock' | 'coin' | 'us_stock',
): string {
  const resolved =
    assetType === 'coin'
      ? COIN_NAMES_KR[code] ?? (name && name !== code ? name : stripKrw(code))
      : name && name !== code
      ? name
      : code;
  return `${resolved} (${code})`;
}

/**
 * 매매일지 작성 페이지
 *
 * URL 파라미터:
 *   - ?ticker=005930        종목 프리셋 (기존 포지션에 거래 추가)
 *   - &type=buy | sell       거래 유형 프리셋 (해당 섹션만 표시)
 *
 * 사용 시나리오:
 *   1) /diary/new              → 종목 검색부터 새로 시작 (새 종목)
 *   2) /diary/new?ticker=X&type=buy  → 기존 종목에 매수 기록 추가
 *   3) /diary/new?ticker=X&type=sell → 기존 종목에 매도 기록 추가
 */
export function DiaryCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search] = useSearchParams();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoginModalOpen(true);
      navigate('/diary', { replace: true });
    }
  }, [isLoggedIn, setLoginModalOpen, navigate]);

  const presetTicker = search.get('ticker');
  const presetType = search.get('type') as 'buy' | 'sell' | null;
  const isAddToExisting = !!presetTicker;

  /**
   * 금액 입력 핸들러 — 콤마 표시용 포맷 문자열과 실제 숫자 문자열을 분리 보관.
   * 미장(us_stock)은 USD 이므로 소수점 2자리까지 허용한다.
   */
  const digitsOnly = (s: string) => s.replace(/[^\d]/g, '');
  const decimalOnly = (s: string) => {
    /* 정수부 + 선택적 .[최대 2자리] */
    const cleaned = s.replace(/[^\d.]/g, '');
    const [head, ...rest] = cleaned.split('.');
    if (rest.length === 0) return head;
    return `${head}.${rest.join('').slice(0, 2)}`;
  };
  const addComma = (s: string) => {
    const d = digitsOnly(s);
    if (!d) return '';
    return Number(d).toLocaleString('ko-KR');
  };
  const formatPriceInput = (raw: string) => {
    if (form.assetType === 'us_stock') return raw;
    return addComma(raw);
  };
  const sanitizePriceInput = (raw: string) => {
    if (form.assetType === 'us_stock') return decimalOnly(raw);
    return digitsOnly(raw);
  };

  /* 오늘 날짜 YYYY-MM-DD (로컬 타임존 기준) — 날짜 픽커 기본값 */
  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const [form, setForm] = useState({
    ticker: presetTicker ?? '',
    tickerLabel: '',
    assetType: 'stock' as 'stock' | 'coin' | 'us_stock',
    buyPrice: '',
    /* presetType=sell 이면 매수일 기본값 공란 (해당 섹션 숨겨짐), 그 외는 오늘 */
    buyDate: presetType === 'sell' ? '' : today,
    sellPrice: '',
    /* presetType=buy 이면 매도일 기본값 공란 (해당 섹션 숨겨짐), 그 외는 오늘 */
    sellDate: presetType === 'buy' ? '' : today,
    quantity: '',
    memo: '',
    // 정책 변경(2026-05-15): 기본값 비공개. 공개 선택 시 포인트 적립.
    isPublic: false,
    // [신규] 선택 항목 (2026-04-21)
    buyReason: '' as '' | BuyReason,
    targetPrice: '',
    stopLoss: '',
    strategyMemo: '',
  });

  /* 기존 종목 추가 모드: ticker 메타를 서버에서 fetch 해 tickerLabel/assetType 프리셋 */
  const tickerMetaQuery = useQuery({
    queryKey: ['tickers', 'meta', presetTicker],
    queryFn: () =>
      get<TickerMeta>(`/tickers/${encodeURIComponent(presetTicker!)}`).catch(
        () => null,
      ),
    enabled: !!presetTicker,
  });

  /* 매도 추가 모드: 현재 보유 수량 조회 */
  const positionQuery = useQuery({
    queryKey: ['diary-position', presetTicker],
    queryFn: async () => {
      try {
        return await get<{
          position: {
            holding_qty: number;
            total_buy_qty: number;
            total_sell_qty: number;
            avg_buy_price: number | null;
          };
        }>(`/diaries/positions/${encodeURIComponent(presetTicker!)}`);
      } catch {
        return null;
      }
    },
    enabled: !!presetTicker && presetType === 'sell',
  });

  const currentHolding = positionQuery.data?.position?.holding_qty ?? 0;
  const totalBuyQty = positionQuery.data?.position?.total_buy_qty ?? 0;
  /* 매도 수량이 보유 수량 초과하는지 — 매수 기록 있을 때만 검증 (공매도 허용) */
  const sellExceedsHolding =
    presetType === 'sell' &&
    totalBuyQty > 0 &&
    form.quantity !== '' &&
    Number(form.quantity) > currentHolding;

  useEffect(() => {
    if (!isAddToExisting) return;
    const meta = tickerMetaQuery.data;
    if (!meta) return;
    setForm((prev) => ({
      ...prev,
      tickerLabel: tickerLabelOf(meta.code, meta.name, meta.assetType),
      assetType: meta.assetType,
    }));
  }, [isAddToExisting, tickerMetaQuery.data]);

  const handleTickerChange = (code: string, meta?: TickerMeta) => {
    setForm((prev) => ({
      ...prev,
      ticker: code,
      tickerLabel: meta ? tickerLabelOf(meta.code, meta.name, meta.assetType) : '',
      assetType: meta?.assetType ?? prev.assetType,
    }));
  };

  /* 입력 쌍 상태 */
  const hasBuyInfo = form.buyPrice !== '' || form.buyDate !== '';
  const buyComplete = form.buyPrice !== '' && form.buyDate !== '';
  const hasSellInfo = form.sellPrice !== '' || form.sellDate !== '';
  const sellComplete = form.sellPrice !== '' && form.sellDate !== '';

  const dateOrderValid =
    !buyComplete || !sellComplete || form.sellDate >= form.buyDate;

  /**
   * 가격/수량 허용 범위 — 백엔드 PRICE_RANGES / QUANTITY_RANGE 와 동기화.
   * 범위를 벗어나면 제출 버튼이 비활성되고 입력 필드 아래 안내가 표시된다.
   */
  const priceRange = PRICE_RANGES[form.assetType];
  const priceUnitStr = priceUnit(form.assetType);
  const priceRangeLabel = `${priceUnitStr}${priceRange.min.toLocaleString()} ~ ${priceUnitStr}${priceRange.max.toLocaleString()}`;

  const outOfPriceRange = (v: string) => {
    if (v === '') return false;
    const n = Number(v);
    return !Number.isFinite(n) || n < priceRange.min || n > priceRange.max;
  };
  const buyPriceInvalid = outOfPriceRange(form.buyPrice);
  const sellPriceInvalid = outOfPriceRange(form.sellPrice);
  const targetPriceInvalid = outOfPriceRange(form.targetPrice);
  const stopLossInvalid = outOfPriceRange(form.stopLoss);

  const quantityInvalid =
    form.quantity !== '' &&
    (!Number.isFinite(Number(form.quantity)) ||
      Number(form.quantity) < QUANTITY_RANGE.min ||
      Number(form.quantity) > QUANTITY_RANGE.max);
  const strategyMemoTooLong = form.strategyMemo.length > 200;

  /* 섹션 표시 여부 — presetType 이 있으면 해당 섹션만 */
  const showBuySection = presetType === 'buy' || !presetType;
  const showSellSection = presetType === 'sell' || !presetType;

  const mutation = useMutation({
    mutationFn: () =>
      post('/diaries', {
        ticker: form.ticker,
        assetType: form.assetType,
        buyPrice: buyComplete ? Number(form.buyPrice) : undefined,
        buyDate: buyComplete ? form.buyDate : undefined,
        sellPrice: sellComplete ? Number(form.sellPrice) : undefined,
        sellDate: sellComplete ? form.sellDate : undefined,
        quantity: Number(form.quantity),
        memo: form.memo || undefined,
        isPublic: form.isPublic,
        // [신규] 선택 항목 — 비어있으면 undefined 로 전송해 서버에서 null 저장
        buyReason: form.buyReason || undefined,
        targetPrice: form.targetPrice ? Number(form.targetPrice) : undefined,
        stopLoss: form.stopLoss ? Number(form.stopLoss) : undefined,
        strategyMemo: form.strategyMemo.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.diaries.mine });
      queryClient.invalidateQueries({ queryKey: queryKeys.diaries.stats });
      queryClient.invalidateQueries({ queryKey: ['diaries', 'positions'] });
      queryClient.invalidateQueries({
        queryKey: ['diary-position', form.ticker],
      });
      // 공개 피드 / 종목별 공개 일지 / 홈 섹션 / 랭킹 전부 갱신
      queryClient.invalidateQueries({ queryKey: ['diaries', 'feed'] });
      queryClient.invalidateQueries({ queryKey: ['diaries', 'public', form.ticker] });
      // 포인트 지급됐을 수 있으니 auth.me 도 갱신
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      // 기존 종목에 추가한 경우 → 해당 종목 상세로, 아니면 목록으로
      if (isAddToExisting) {
        navigate(`/diary/ticker/${encodeURIComponent(form.ticker)}`);
      } else {
        navigate('/diary');
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '저장에 실패했어요.');
    },
  });

  const profit =
    buyComplete && sellComplete && form.quantity
      ? (Number(form.sellPrice) - Number(form.buyPrice)) *
        Number(form.quantity)
      : 0;

  /**
   * 예상 적립 포인트 — 공개 ON 전제로 현재 입력 상태 기반 계산.
   * (서버 멱등 키로 실제 중복 지급은 방지됨 — 여기는 UX 용 힌트)
   *   공개 기본 +7P / 매수 이유 +3P / 목표가 +2P / 손절가 +2P / 매수+매도 완결 +5P
   */
  const estimatedPoints =
    7 +
    (form.buyReason ? 3 : 0) +
    (form.targetPrice && !targetPriceInvalid ? 2 : 0) +
    (form.stopLoss && !stopLossInvalid ? 2 : 0) +
    (buyComplete && sellComplete ? 5 : 0);

  const canSubmit =
    !!form.ticker &&
    !!form.quantity &&
    !quantityInvalid &&
    (buyComplete || sellComplete) &&
    (!hasBuyInfo || buyComplete) &&
    (!hasSellInfo || sellComplete) &&
    dateOrderValid &&
    !sellExceedsHolding &&
    !buyPriceInvalid &&
    !sellPriceInvalid &&
    !targetPriceInvalid &&
    !stopLossInvalid &&
    !strategyMemoTooLong;

  /* 버튼 라벨 */
  const submitLabel =
    presetType === 'buy'
      ? '매수 추가'
      : presetType === 'sell'
      ? '매도 추가'
      : buyComplete && sellComplete
      ? '저장'
      : buyComplete
      ? '보유 중으로 저장'
      : sellComplete
      ? '매도 기록 저장'
      : '저장';

  /* 페이지 제목/안내 */
  const pageTitle = isAddToExisting
    ? presetType === 'buy'
      ? '매수 거래 추가'
      : presetType === 'sell'
      ? '매도 거래 추가'
      : '거래 추가'
    : '새 종목 추가';

  return (
    <div className="container-base py-10">
      <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6 sm:p-8">
      <header className="mb-6">
        <h1 className="text-3xl font-extrabold">{pageTitle}</h1>
        <p className="mt-2 text-sm text-[var(--color-text-sub)]">
          {isAddToExisting
            ? '기존 종목에 추가 거래를 기록해 보세요.'
            : '매수 또는 매도 기록을 남기면 수익률·보유 현황이 자동 집계돼요.'}
        </p>
      </header>

      {/* 허위 방지 안내 배너 — 모든 모드 공통 */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--color-rise)]" />
        <p className="text-xs leading-relaxed text-[var(--color-text-sub)]">
          매매일지는 실제 거래 기록을 바탕으로 작성해 주세요.
          <br />
          허위 작성 시 이용이 제한될 수 있어요.
        </p>
      </div>

      {!isAddToExisting && (
        <p className="mb-6 text-xs text-[var(--color-text-sub)]">
          매수 또는 매도 중 <strong>하나 이상</strong> 입력하면 저장할 수 있어요.
          <br />
          · 매수만 → 보유 중 기록 &nbsp; · 둘 다 → 완결된 매매
        </p>
      )}
      {isAddToExisting && tickerMetaQuery.isLoading && (
        <p className="mb-6 text-xs text-[var(--color-text-sub)]">종목 정보 로딩 중...</p>
      )}

      <div className="space-y-4">
        {/* 종목 검색 — 기존 종목 추가 모드에서는 고정 (수정 불가) */}
        <Field label="종목">
          {isAddToExisting ? (
            <div className="flex h-10 items-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-sm">
              <span className="font-medium">
                {form.tickerLabel || form.ticker}
              </span>
              <span className="ml-2 text-xs text-[var(--color-text-sub)]">
                {form.assetType === 'coin'
                  ? '코인'
                  : form.assetType === 'us_stock'
                  ? '해외주식'
                  : '국내주식'}
              </span>
            </div>
          ) : (
            <>
              <TickerAutocomplete
                value={form.ticker}
                displayLabel={form.tickerLabel}
                onChange={handleTickerChange}
              />
              {/* 레이아웃 흔들림 방지 — 항상 노출, 선택 전엔 흐리게 */}
              <p
                className={`mt-1 text-xs transition-colors ${
                  form.ticker ? 'text-[var(--color-text-sub)]' : 'text-[var(--color-border)]'
                }`}
              >
                자산 종류: {form.ticker
                  ? form.assetType === 'coin'
                    ? '코인'
                    : form.assetType === 'us_stock'
                    ? '해외주식 (USD)'
                    : '국내주식'
                  : '종목을 선택하면 자동 설정돼요'}
              </p>
            </>
          )}
        </Field>

        <Field label="수량">
          <Input
            type="text"
            inputMode="decimal"
            value={form.quantity}
            onChange={(e) => {
              // 소수점 1개까지 허용 (코인은 소수점 수량 보편)
              const cleaned = e.target.value.replace(/[^\d.]/g, '');
              const [head, ...rest] = cleaned.split('.');
              const normalized = rest.length === 0 ? head : `${head}.${rest.join('')}`;
              setForm({ ...form, quantity: normalized });
            }}
            placeholder="필수"
            error={sellExceedsHolding || quantityInvalid}
          />
          {/* 수량 범위 안내 (매도 초과 검사와 우선순위: 매도 초과 > 범위 초과) */}
          {quantityInvalid && !sellExceedsHolding && (
            <p className="mt-1 text-xs font-medium text-[var(--color-rise)]">
              수량은 {QUANTITY_RANGE.min} ~ {QUANTITY_RANGE.max.toLocaleString()} 범위여야 해요.
            </p>
          )}
          {/* 매도 모드: 현재 보유 수량 안내 */}
          {presetType === 'sell' && !positionQuery.isLoading && (
            <>
              {totalBuyQty > 0 ? (
                <p
                  className={`mt-1 text-xs ${
                    sellExceedsHolding
                      ? 'text-[var(--color-rise)] font-medium'
                      : 'text-[var(--color-text-sub)]'
                  }`}
                >
                  {sellExceedsHolding ? (
                    <>⚠ 보유 수량({currentHolding}개)을 초과해서 매도할 수 없어요.</>
                  ) : (
                    <>현재 보유 수량: <strong>{currentHolding}개</strong> (최대)</>
                  )}
                </p>
              ) : (
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                  매수 기록이 없는 종목이에요 (공매도/선물로 처리)
                </p>
              )}
            </>
          )}
        </Field>

        {/* 매수 정보 */}
        {showBuySection && (
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-semibold text-[var(--color-text-sub)]">
                🟢 매수 정보 {presetType === 'buy' ? '(필수)' : '(선택)'}
              </div>
              {hasBuyInfo && !presetType && (
                <button
                  type="button"
                  onClick={() =>
                    setForm({ ...form, buyPrice: '', buyDate: '' })
                  }
                  className="text-xs text-[var(--color-primary)] hover:underline"
                >
                  비우기
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={form.assetType === 'us_stock' ? '매수가 ($)' : '매수가'}>
                <Input
                  type="text"
                  inputMode={form.assetType === 'us_stock' ? 'decimal' : 'numeric'}
                  value={formatPriceInput(form.buyPrice)}
                  onChange={(e) =>
                    setForm({ ...form, buyPrice: sanitizePriceInput(e.target.value) })
                  }
                  placeholder={presetType === 'buy' ? '필수' : '선택'}
                />
              </Field>
              <Field label="매수일">
                <DatePicker
                  value={form.buyDate}
                  onChange={(v) => setForm({ ...form, buyDate: v })}
                  max={today}
                  ariaLabel="매수일"
                />
              </Field>
            </div>
            {/* 레이아웃 고정 — 항상 한 줄 공간 점유, 상황에 따라 색만 변경 */}
            <p
              className={`mt-2 text-xs transition-colors ${
                buyPriceInvalid
                  ? 'text-[var(--color-rise)] font-medium'
                  : hasBuyInfo && !buyComplete
                  ? 'text-[var(--color-rise)]'
                  : 'text-[var(--color-border)]'
              }`}
            >
              {buyPriceInvalid
                ? `매수가는 ${priceRangeLabel} 범위로 입력해 주세요.`
                : '매수가와 매수일은 함께 입력해야 해요.'}
            </p>
          </div>
        )}

        {/* 매도 정보 */}
        {showSellSection && (
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-semibold text-[var(--color-text-sub)]">
                🔴 매도 정보 {presetType === 'sell' ? '(필수)' : '(선택)'}
              </div>
              {hasSellInfo && !presetType && (
                <button
                  type="button"
                  onClick={() =>
                    setForm({ ...form, sellPrice: '', sellDate: '' })
                  }
                  className="text-xs text-[var(--color-primary)] hover:underline"
                >
                  비우기
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={form.assetType === 'us_stock' ? '매도가 ($)' : '매도가'}>
                <Input
                  type="text"
                  inputMode={form.assetType === 'us_stock' ? 'decimal' : 'numeric'}
                  value={formatPriceInput(form.sellPrice)}
                  onChange={(e) =>
                    setForm({ ...form, sellPrice: sanitizePriceInput(e.target.value) })
                  }
                  placeholder={presetType === 'sell' ? '필수' : '선택'}
                />
              </Field>
              <Field label="매도일">
                <DatePicker
                  value={form.sellDate}
                  onChange={(v) => setForm({ ...form, sellDate: v })}
                  min={form.buyDate || undefined}
                  max={today}
                  ariaLabel="매도일"
                />
              </Field>
            </div>
            <p
              className={`mt-2 text-xs transition-colors ${
                sellPriceInvalid
                  ? 'text-[var(--color-rise)] font-medium'
                  : hasSellInfo && !sellComplete
                  ? 'text-[var(--color-rise)]'
                  : 'text-[var(--color-border)]'
              }`}
            >
              {sellPriceInvalid
                ? `매도가는 ${priceRangeLabel} 범위로 입력해 주세요.`
                : '매도가와 매도일은 함께 입력해야 해요.'}
            </p>
          </div>
        )}

        {buyComplete && sellComplete && !dateOrderValid && (
          <p className="text-xs text-[var(--color-rise)]">
            매도일은 매수일과 같거나 이후여야 해요.
          </p>
        )}

        {/* ── 선택 항목 (2026-04-21) ─────────────────────────────────── */}
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--color-text-sub)]">
              ✨ 선택 항목 — 입력하면 추가 포인트
            </span>
          </div>

          {/* 매수 이유 — 라디오 칩 */}
          <Field
            label={
              <span className="flex items-center gap-1.5">
                매수 이유 <span className="text-[var(--color-text-sub)]">(선택)</span>
                {form.buyReason && (
                  <PointBadge amount={3} />
                )}
              </span>
            }
          >
            <div className="flex flex-wrap gap-2">
              {BUY_REASON_CHIPS.map(({ value, label }) => {
                const active = form.buyReason === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        buyReason: active ? '' : value,
                      })
                    }
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      active
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                        : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* 목표가 / 손절가 — 두 칸 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label={
                <span className="flex items-center gap-1.5">
                  목표가 <span className="text-[var(--color-text-sub)]">(선택)</span>
                  {form.targetPrice && !targetPriceInvalid && (
                    <PointBadge amount={2} />
                  )}
                </span>
              }
            >
              <Input
                type="text"
                inputMode={form.assetType === 'us_stock' ? 'decimal' : 'numeric'}
                value={formatPriceInput(form.targetPrice)}
                onChange={(e) =>
                  setForm({ ...form, targetPrice: sanitizePriceInput(e.target.value) })
                }
                placeholder={`${priceUnitStr}0${priceSuffix(form.assetType)}`}
                error={targetPriceInvalid}
              />
              {targetPriceInvalid && (
                <p className="mt-1 text-xs font-medium text-[var(--color-rise)]">
                  {priceRangeLabel} 범위로 입력해 주세요.
                </p>
              )}
            </Field>
            <Field
              label={
                <span className="flex items-center gap-1.5">
                  손절가 <span className="text-[var(--color-text-sub)]">(선택)</span>
                  {form.stopLoss && !stopLossInvalid && (
                    <PointBadge amount={2} />
                  )}
                </span>
              }
            >
              <Input
                type="text"
                inputMode={form.assetType === 'us_stock' ? 'decimal' : 'numeric'}
                value={formatPriceInput(form.stopLoss)}
                onChange={(e) =>
                  setForm({ ...form, stopLoss: sanitizePriceInput(e.target.value) })
                }
                placeholder={`${priceUnitStr}0${priceSuffix(form.assetType)}`}
                error={stopLossInvalid}
              />
              {stopLossInvalid && (
                <p className="mt-1 text-xs font-medium text-[var(--color-rise)]">
                  {priceRangeLabel} 범위로 입력해 주세요.
                </p>
              )}
            </Field>
          </div>

          {/* 투자 전략 메모 — 메모와 별도, 200자 */}
          <Field
            label={
              <span className="flex items-center gap-1.5">
                투자 전략 <span className="text-[var(--color-text-sub)]">(선택, 200자 이내)</span>
              </span>
            }
          >
            <Textarea
              value={form.strategyMemo}
              onChange={(e) => setForm({ ...form, strategyMemo: e.target.value })}
              rows={2}
              maxLength={200}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
              placeholder="예: 장기 보유 예정, 돌파 시 추가 매수"
            />
            <p className="mt-1 text-right text-[10px] text-[var(--color-text-sub)]">
              {form.strategyMemo.length}/200
            </p>
          </Field>
        </div>

        <Field label="메모 (선택)">
          <Textarea
            value={form.memo}
            onChange={(e) => setForm({ ...form, memo: e.target.value })}
            rows={4}
            maxLength={2000}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
            placeholder="매매 이유, 배운 점 등"
          />
        </Field>

        {/* 공개/비공개 토글 — 기본 비공개. 공개 시 포인트 적립 안내 강조 */}
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">
                {form.isPublic ? '🌐 공개' : '🔒 비공개 (기본)'}
              </div>
              <div className="mt-0.5 text-xs text-[var(--color-text-sub)]">
                {form.isPublic
                  ? `피드에서 다른 유저에게 공개돼요 (기본 +7P · 예상 적립 +${estimatedPoints}P)`
                  : '나만 볼 수 있어요'}
              </div>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={form.isPublic}
                onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
              />
              <div className="peer h-6 w-11 rounded-full bg-[var(--color-border)] after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-[var(--color-primary)] peer-checked:after:translate-x-5" />
            </label>
          </div>
          {!form.isPublic && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">
              공개하면 포인트가 쌓여요! 🎁 (기본 +7P · 예상 적립 +{estimatedPoints}P)
            </p>
          )}
          {/* 비공개 작성자도 AI 분석에 익명으로 참여한다는 점 안내 — 공개·비공개 무관 항상 노출 */}
          <p className="mt-2 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
            ℹ️ 비공개로 작성하셔도 작성자와 인원수가 드러나지 않는 방식으로 AI 분석에 참고될 수 있습니다.
          </p>
        </div>

        {buyComplete && sellComplete && profit !== 0 && (
          <div className="rounded-lg bg-[var(--color-bg)] p-3 text-center">
            <div className="text-xs text-[var(--color-text-sub)]">
              예상 손익
            </div>
            <div
              className={
                profit >= 0
                  ? 'text-[var(--color-rise)]'
                  : 'text-[var(--color-fall)]'
              }
            >
              <span className="text-xl font-bold">
                {form.assetType === 'us_stock'
                  ? `${profit >= 0 ? '+' : '-'}$${Math.abs(profit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : `${profit >= 0 ? '+' : ''}${profit.toLocaleString()}원`}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-2">
        <Button variant="ghost" fullWidth onClick={() => navigate(-1)}>
          취소
        </Button>
        <Button
          fullWidth
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!canSubmit}
        >
          {submitLabel}
        </Button>
      </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

/** 선택 항목 입력 시 "+NP" 점수 배지 — 현재 입력 상태 힌트 */
function PointBadge({ amount }: { amount: number }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[10px] font-bold text-[var(--color-primary)]">
      +{amount}P
    </span>
  );
}
